import React, {useEffect, useState} from "react";
import { IPageResponse, SortDirection } from "common/interfaces/IPageResponse";
import { IPageRequest } from "common/interfaces/IPageRequest";
import AwesomeDebouncePromise from "awesome-debounce-promise";
import { ElectricityAgreementRenewalPlan, ElectricityAgreementRenewalPlanFilters, ElectricityAgreementRenewalPlanSortFields, ElectricityAgreementRenewalPlanStatus, ElectricityAgreementRenewalPlanType } from "components/ElectricityAgreementRenewalPlans/models";
import { ElectricityAgreementRenewalPlansSearchBar } from "components/ElectricityAgreementRenewalPlans/ElectricityAgreementRenewalPlansSearchBar";
import { ElectricityAgreementRenewalPlansTable } from "components/ElectricityAgreementRenewalPlans/ElectricityAgreementRenewalPlansTable";
import { ElectricityAgreementRenewalPlansService } from "components/ElectricityAgreementRenewalPlans/ElectricityAgreementRenewalPans.service";
import { UpdateElectricityAgreementRenewalPlanModal } from "components/ElectricityAgreementRenewalPlans/modals/UpdateElectricityAgreementRenewalPlanModal";
import { CancelElectricityAgreementRenewalPlanConfirmModal } from "components/ElectricityAgreementRenewalPlans/modals/CancelElectricityAgreementRenewalPlanConfirmModal";
import moment from "moment";
import { isoDateFormat } from "common/dateFormatterConfig";
import { Button } from "react-bootstrap";
import { IElectricityPackage } from "components/ElectricityPackages/models";
import { ElectricityPackagesService } from "components/ElectricityPackages/ElectricityPackages.service";

interface ElectricityAgreementRenewalPlansProps {
    onLoadValidatePage: () => void;
}

const searchApi = AwesomeDebouncePromise(
    request => ElectricityAgreementRenewalPlansService.getAgreementRenewalPlans(request),
    300
);

const ElectricityAgreementRenewalPlans = (props : ElectricityAgreementRenewalPlansProps) => {
    const [renewalPlans, setRenewalPlans] = useState<IPageResponse<ElectricityAgreementRenewalPlan>>();
    const [packages, setPackages] = useState<IElectricityPackage[]>([]);
    const [pageRequest, setPageRequest] = useState<IPageRequest<ElectricityAgreementRenewalPlanSortFields>>({
        page: 0,
        size: 20,
        sortDirection: SortDirection.ASC,
        sortField: ElectricityAgreementRenewalPlanSortFields.ID
    });
    
    const [activeRenewalPlan, setActiveRenewalPlan] = useState<ElectricityAgreementRenewalPlan>();
    const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
    const [isCancelModalVisible, setIsCancelModalVisible] = useState(false);

    const [selectedRenewalPlanIds, setSelectedRenewalPlanIds] = useState<number[]>([]);
    const [excludedRenewalPlanIds, setExcludedRenewalPlanIds] = useState<number[]>([]);
    const [allRenewalPlansSelected, setAllRenewalPlansSelected] = useState(false);
    
    const [filters, setFilters] = useState<ElectricityAgreementRenewalPlanFilters>({});
    const [isRequesting, setIsRequesting] = useState(false);

    const searchRenewalPlans = async (request: any, setLoader: boolean) => {
            try {
                if(setLoader) setIsRequesting(true);
                const plans = await searchApi(request);
                setRenewalPlans(plans)
            } catch(e) {
                console.error(e);
            } finally {
                setIsRequesting(false)
            }
    };

    const onPageChange = (targetPage: number) => {
        const page = targetPage - 1;
        setPageRequest({
            ...pageRequest,
            page
        })

        searchRenewalPlans({
            ...pageRequest,
            ...filters,
            page
        }, true)
    };

    const handleSortClick = (sortField: ElectricityAgreementRenewalPlanSortFields) => {
            let sortDirection = SortDirection.ASC;
            if (pageRequest.sortField.match(sortField)) {
                sortDirection = pageRequest.sortDirection === SortDirection.DESC ? SortDirection.ASC : SortDirection.DESC;
            }

            setPageRequest({
                ...pageRequest,
                sortDirection,
                sortField
            })

            searchRenewalPlans({
                ...pageRequest,
                ...filters,
                sortDirection,
                sortField
            }, true)
    };

    const handleFilterChange = (change: ElectricityAgreementRenewalPlanFilters) => {
        setRenewalPlans(undefined);
        setPageRequest({
            ...pageRequest,
            page: 0
        })
        setFilters({
            ...filters,
            ...change
        })
        setSelectedRenewalPlanIds([]);
        setAllRenewalPlansSelected(false)
        setExcludedRenewalPlanIds([]);

        searchRenewalPlans({
            ...pageRequest,
            ...filters,
            ...change,
            page: 0
        }, true)
    };

    const initializeRequests = async (isInitial: boolean) => {
        await searchRenewalPlans({
            ...pageRequest,
            ...filters,
            page: isInitial ? 0 : pageRequest.page
        }, isInitial);
    }

    const onPeriodChange = (periodStart?: string, periodEnd?: string) => {
            const offerSendDateStart = periodStart ? moment(new Date(periodStart)).format(isoDateFormat) : undefined;
            const offerSendDateEnd = periodEnd ? moment(new Date(periodEnd)).format(isoDateFormat) : undefined;

            setSelectedRenewalPlanIds([]);
            setAllRenewalPlansSelected(false)
            setExcludedRenewalPlanIds([]);

            setFilters({
                ...filters,
                offerSendDateStart,
                offerSendDateEnd
            });

            searchRenewalPlans({
                ...pageRequest,
                ...filters,
                page: 0,
                offerSendDateStart,
                offerSendDateEnd
            }, true);
    }

    const onRenewalPlanSelect = (renewalPlan: ElectricityAgreementRenewalPlan, state: boolean) => {
        if (state === false) {
            if (allRenewalPlansSelected) {
                setExcludedRenewalPlanIds(
                    [...excludedRenewalPlanIds, renewalPlan.id]
                )
            } else {
                const filtered = selectedRenewalPlanIds.filter(id => id !== renewalPlan.id);
                setSelectedRenewalPlanIds([...filtered]);
            }
        } else {
            if (allRenewalPlansSelected) {
                const filtered = excludedRenewalPlanIds.filter(id => id !== renewalPlan.id);
                setExcludedRenewalPlanIds([...filtered]);
            } else {
                setSelectedRenewalPlanIds(
                    [...selectedRenewalPlanIds, renewalPlan.id]
                )
            }
        }
    };

    const updateRenewalPlan = (renewalPlan: ElectricityAgreementRenewalPlan) => {
        setActiveRenewalPlan(renewalPlan);
        setIsUpdateModalVisible(true);
    }

    const cancelRenewalPlan = (renewalPlan: ElectricityAgreementRenewalPlan) => {
        setActiveRenewalPlan(renewalPlan);
        setIsCancelModalVisible(true);
    }

    useEffect(() => {
        props.onLoadValidatePage();
        initializeRequests(true);

        (async () => {
            const response = await ElectricityPackagesService.getElectricityPackages({
                page: 0,
                size: 999
            });

            setPackages(response.content);
        })();
          // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const updateAllRenewalPlans = () => {
        setIsUpdateModalVisible(true);
    }

    const showEditAll = allRenewalPlansSelected || !!selectedRenewalPlanIds.length;
    const showSelectAll =
        filters.type !== ElectricityAgreementRenewalPlanType.PROCUREMENT &&
        (filters.status === ElectricityAgreementRenewalPlanStatus.INCOMPLETE ||
        filters.status === ElectricityAgreementRenewalPlanStatus.PLANNED ||
        !filters.status);

    return (
        <div className="container">
            <div className="row">
                <div className="col">
                    <ElectricityAgreementRenewalPlansSearchBar
                        onFilter={handleFilterChange} 
                        filters={filters}
                        onPeriodChange={onPeriodChange}
                        packages={packages}
                    />
                    <div className="d-flex mb-1" style={{visibility: showEditAll ? 'visible' : "hidden"}}>
                        <Button 
                            className="ml-3 d-flex align-items-center"
                            onClick={() => updateAllRenewalPlans()}
                            variant={"secondary"}
                        >
                            <i className="icon--16 icon__edit d-flex mr-1"/>
                        </Button>
                    </div>
                    <ElectricityAgreementRenewalPlansTable 
                        onPageChange={onPageChange}
                        onSort={handleSortClick}
                        renewalPlans={renewalPlans}
                        sortDirection={pageRequest.sortDirection}
                        sortField={pageRequest.sortField}
                        isRequesting={isRequesting}
                        updateAgreementRenewalPlan={updateRenewalPlan}
                        cancelAgreementRenewalPlan={cancelRenewalPlan}
                        selectedRenewalPlanIds={selectedRenewalPlanIds}
                        setSelectedRenewalPlanIds={setSelectedRenewalPlanIds}
                        excludedRenewalPlanIds={excludedRenewalPlanIds}
                        setExcludedRenewalPlanIds={setExcludedRenewalPlanIds}
                        onRenewalPlanSelect={onRenewalPlanSelect}
                        allRenewalPlansSelected={allRenewalPlansSelected}
                        setAllRenewalPlansSelected={setAllRenewalPlansSelected}
                        showSelectAll={showSelectAll}
                    />
                </div>
            </div>    
           {
            isUpdateModalVisible ? (
              <UpdateElectricityAgreementRenewalPlanModal 
                renewalPlan={activeRenewalPlan}
                handleModalHide={() => {
                  setActiveRenewalPlan(undefined);
                  setIsUpdateModalVisible(false);
                }}
                onSuccess={() => initializeRequests(false)}
                filters={filters}
                selectedRenewalPlanIds={selectedRenewalPlanIds}
                excludedRenewalPlanIds={excludedRenewalPlanIds}
                allRenewalPlansSelected={allRenewalPlansSelected}
                packages={packages}
              />
            ) : null
           }
           {
            isCancelModalVisible && activeRenewalPlan ? (
              <CancelElectricityAgreementRenewalPlanConfirmModal 
                renewalAgreementId={activeRenewalPlan.id}
                initializeRequests={initializeRequests}
                handleConfirmSubmit={() => {
                  setActiveRenewalPlan(undefined);
                  setIsCancelModalVisible(false);
                }}
                handleConfirmReject={() => {
                  setActiveRenewalPlan(undefined);
                  setIsCancelModalVisible(false);
                }}
              />
            ) : null
           }
        </div>
    );
}

export default ElectricityAgreementRenewalPlans;